import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Select from 'react-select';
import { TimePicker } from 'antd';

import { Badge, Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from 'reactstrap';

import TickIcon from '../../../../../../../assets/images/tick.png';
import UntickIcon from '../../../../../../../assets/images/untick.png';
import {
  CONFIRM_BOOKING_BY_VENDOR,
  CONFIRM_BOOKING_BY_VENDOR_EMPTY,
  GET_REASON_BY_ACTIONS,
  GET_REJECT_SLOT_OPTIONS_V2,
} from '../../../../../../../store/actions';
import cogoToast from 'cogo-toast';

const ConfirmBooking = ({ currentSelectedBooking, onClose, bookingDetails }) => {
  const dispatch = useDispatch();

  const customer = {
    name: bookingDetails?.bookingDetails?.customer_name ?? '',
    players: bookingDetails?.bookingDetails?.number_of_players ?? '',
    guests: bookingDetails?.bookingDetails?.number_of_guests ?? '',
    nationality: bookingDetails?.bookingDetails?.nationality ?? '',
    addOns: bookingDetails?.bookingDetails?.add_ons?.split(',') ?? [],
  };

  const [selectedPreferredSlot, setSelectedPreferredSlot] = useState(null);
  const [preferredSlots, setPreferredSlots] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [selectedRemark, setSelectedRemark] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  const [selectedSpecificSlotTime, setSelectedSpecificSlotTime] = useState(null);

  console.log('availableSlots1212', {
    selectedPreferredSlot,
    preferredSlots,
    availableSlots,
    selectedTimeSlot,
  });

  /* ---------------------------- REDUX STATES ---------------------------- */
  const { confirmByVendor, confirmByVendorloading, remarksList, slotOptionsRes } = useSelector(state => ({
    confirmByVendor: state?.ConfirmBookingByVendor?.data,
    confirmByVendorloading: state?.ConfirmBookingByVendor?.loading,
    remarksList: state?.GetReasonByActions?.data,
    slotOptionsRes: state?.GetRejectSlotOptionsV2?.data,
  }));

  const transformSlots = data => {
    const grouped = {};
    data?.slots?.forEach(slot => {
      const { slotDate, slotId, slotTime, remainingQuota, isActive } = slot;
      if (!grouped[slotDate]) grouped[slotDate] = [];
      grouped[slotDate].push({ slotId, value: slotTime, remainingQuota, isActive });
    });

    return Object.keys(grouped).map(date => ({
      slotDate: date,
      value: grouped[date],
    }));
  };

  const fetchSlotData = preferredSlot => {
    dispatch({
      type: GET_REJECT_SLOT_OPTIONS_V2,
      payload: {
        data: {
          booking_id: currentSelectedBooking?.id,
          preferred_date_1: preferredSlot.id === 'slot1',
          preferred_date_2: preferredSlot.id === 'slot2',
        },
      },
    });
  };

  useEffect(() => {
    const transformed = transformSlots(slotOptionsRes);
    setAvailableSlots(transformed);
  }, [slotOptionsRes]);

  useEffect(() => {
    const slot1 = bookingDetails?.bookingDetails?.prefferedDateTime1
      ? { id: 'slot1', label: 'Preferred Slot 1', time: bookingDetails?.bookingDetails?.prefferedDateTime1 }
      : null;
    const slot2 = bookingDetails?.bookingDetails?.prefferedDateTime2
      ? { id: 'slot2', label: 'Preferred Slot 2', time: bookingDetails?.bookingDetails?.prefferedDateTime2 }
      : null;
    const slots = [slot1, slot2].filter(Boolean);
    setPreferredSlots(slots);
    if (slot1) {
      setSelectedPreferredSlot(slot1);
      fetchSlotData(slot1);
    } else if (slot2) {
      setSelectedPreferredSlot(slot2);
      fetchSlotData(slot2);
    }
  }, [bookingDetails]);

  useEffect(() => {
    dispatch({
      type: GET_REASON_BY_ACTIONS,
      payload: {
        urlParams: {
          booking_type: currentSelectedBooking?.bookingType,
          booking_status: currentSelectedBooking?.bookingStatus,
          action: 'confirm_booking',
          is_active: true,
          page_size: 50,
          page_index: 0,
        },
      },
    });
  }, [currentSelectedBooking]);

  useEffect(() => {
    if (confirmByVendor !== null) {
      dispatch({ type: CONFIRM_BOOKING_BY_VENDOR_EMPTY });
      onClose();
    }
    return () => {
      dispatch({ type: CONFIRM_BOOKING_BY_VENDOR_EMPTY });
    };
  }, [confirmByVendor]);

  const handleSubmit = () => {
    if (!selectedTimeSlot) {
      cogoToast.error('Please select slot time');
      return;
    }
    setFormSubmitted(true);
    if (selectedRemark) {
      const confirmBookingPayload = {
        booking_id: currentSelectedBooking?.id,
        preferred_date_1: selectedPreferredSlot?.id === 'slot1',
        preferred_date_2: selectedPreferredSlot?.id === 'slot2',
        slot_id: selectedTimeSlot?.slotId,
        confirmation_number: confirmationNumber,
        reason_id: selectedRemark?.value,
        slot_time: selectedSpecificSlotTime ? selectedSpecificSlotTime.format('HH:mm') : selectedTimeSlot?.value,
      };

      dispatch({
        type: CONFIRM_BOOKING_BY_VENDOR,
        payload: {
          body: {
            ...confirmBookingPayload,
          },
        },
      });
    }
  };

  const { disabledHours, disabledMinutes } = useMemo(() => {
    if (!selectedPreferredSlot) {
      return { disabledHours: () => [], disabledMinutes: () => [] };
    }

    if (selectedPreferredSlot.id === 'slot1') {
      return getDisabledTimeRange(
        bookingDetails?.bookingDetails?.prefferedDateTime1ConfirmedTimeFrom,
        bookingDetails?.bookingDetails?.prefferedDateTime1ConfirmedTimeTo,
      );
    }

    if (selectedPreferredSlot.id === 'slot2') {
      return getDisabledTimeRange(
        bookingDetails?.bookingDetails?.prefferedDateTime2ConfirmedTimeFrom,
        bookingDetails?.bookingDetails?.prefferedDateTime2ConfirmedTimeTo,
      );
    }

    return { disabledHours: () => [], disabledMinutes: () => [] };
  }, [selectedPreferredSlot, bookingDetails]);

  return (
    <Modal isOpen={true} size="md">
      <ModalHeader toggle={onClose}>Confirm</ModalHeader>
      <ModalBody>
        <p className="text-muted">Are you sure you want to confirm this booking</p>
        <FormGroup tag="fieldset" className="mb-4">
          {preferredSlots.map(slot => {
            const isSelected = selectedPreferredSlot?.id === slot.id;
            return (
              <div key={slot.id}>
                <div
                  onClick={() => {
                    setSelectedPreferredSlot(slot);
                    fetchSlotData(slot);
                    setSelectedTimeSlot(null);
                    setSelectedSpecificSlotTime(null);
                  }}
                  className={`d-flex align-items-center p-3 mb-2 rounded ${isSelected ? 'border border-success' : 'border border-light'}`}
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{ width: 24, height: 24, marginRight: 12 }}>
                    <img src={isSelected ? TickIcon : UntickIcon} width={24} height={24} alt="tick" />
                  </div>
                  <div className="flex-grow-1 font-weight-bold">{slot.label}</div>
                  <div className="font-weight-bold text-right">{slot.time}</div>
                </div>

                {isSelected &&
                  availableSlots?.map(group => (
                    <div className="slot-container mt-3" key={group.slotDate}>
                      <h6 className="mb-3">Available Slots on {group.slotDate}</h6>
                      <div className="slot-scroll">
                        {group.value.map(slotItem => {
                          const isSelectedSlot = slotItem?.slotId === selectedTimeSlot?.slotId;
                          return (
                            <span
                              key={slotItem.slotId}
                              className="slot-btn cursor-pointer"
                              onClick={() => {
                                setSelectedTimeSlot(slotItem);
                              }}
                              style={{ background: isSelectedSlot ? '#abb9e8' : 'white' }}
                            >
                              {moment(slotItem.value, 'HH:mm').format('hh:mm A')}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
              </div>
            );
          })}
        </FormGroup>

        <Row className="mb-4">
          <Col xs="12">
            <Label for="remarksSelect">
              <strong>Specific Time for Slot</strong>
            </Label>
          </Col>
          <Col xs="12">
            <TimePicker
              className="w-100"
              use12Hours
              format="h:mm a"
              disabledHours={disabledHours}
              disabledMinutes={disabledMinutes}
              value={selectedSpecificSlotTime}
              onChange={time => {
                console.log('selected time:', time ? time.format('HH:mm') : null);

                setSelectedSpecificSlotTime(time);
              }}
            />
          </Col>
        </Row>

        <Row className="mb-4">
          <Col xs="6">
            <strong>Customer name</strong>
          </Col>
          <Col xs="6" className="text-right">
            {customer?.name}
          </Col>
          <Col xs="6">
            <strong>No. of Players</strong>
          </Col>
          <Col xs="6" className="text-right">
            {customer?.players ?? 0}
          </Col>
          <Col xs="6">
            <strong>No. of Guests</strong>
          </Col>
          <Col xs="6" className="text-right">
            {customer?.guests ?? 0}
          </Col>
          <Col xs="6">
            <strong>Add-ons</strong>
          </Col>
          <Col xs="6" className="text-right">
            {customer.addOns.map((a, index) => (
              <Badge pill className="addon-badge mr-2 border" key={index}>
                {a}
              </Badge>
            ))}
          </Col>
        </Row>

        <FormGroup>
          <Label>
            <strong>
              Remarks <span className="lbl_mandatory">*</span>
            </strong>
          </Label>
          <Select
            placeholder="Select reason for confirming the booking"
            value={selectedRemark}
            options={remarksList?.reasons?.map(r => ({ label: r.reason, value: r.id }))}
            onChange={setSelectedRemark}
          />
          {formSubmitted && selectedRemark === null && <div className="text-danger">Remarks are required</div>}
        </FormGroup>

        <FormGroup>
          <Label>
            <strong>Confirmation Number</strong>
          </Label>
          <Input
            type="text"
            value={confirmationNumber}
            onChange={e => {
              const input = e.target.value;
              setConfirmationNumber(input); // no formatting or restriction
            }}
            placeholder="Enter confirmation number"
          />
        </FormGroup>
      </ModalBody>

      <div className="d-flex justify-content-center mb-4 gap-3">
        <Button
          color="primary"
          style={{ fontSize: '18px', width: '217px', borderRadius: '10px' }}
          onClick={handleSubmit}
          disabled={confirmByVendorloading}
        >
          {confirmByVendorloading ? <Spinner size="sm" /> : 'Yes'}
        </Button>

        <Button color="white" style={{ fontSize: '18px', width: '217px', borderRadius: '10px', border: '1px solid #401589' }} onClick={onClose}>
          No
        </Button>
      </div>
    </Modal>
  );
};

export default ConfirmBooking;

function getDisabledTimeRange(startTime, endTime) {
  if (!startTime || !endTime) {
    // no restrictions → allow everything
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  }

  const start = moment(startTime, 'HH:mm');
  const end = moment(endTime, 'HH:mm');

  const startHour = start.hour();
  const startMinute = start.minute();
  const endHour = end.hour();
  const endMinute = end.minute();

  const disabledHours = () => {
    const disabled = [];
    for (let i = 0; i < 24; i++) {
      if (i < startHour || i > endHour) {
        disabled.push(i);
      }
    }
    return disabled;
  };

  const disabledMinutes = selectedHour => {
    let minutes = [];
    if (selectedHour === startHour) {
      // disable before startMinute
      minutes = minutes.concat(Array.from({ length: startMinute }, (_, i) => i));
    }
    if (selectedHour === endHour) {
      // disable after endMinute
      minutes = minutes.concat(Array.from({ length: 59 - endMinute }, (_, i) => i + endMinute + 1));
    }
    return minutes;
  };

  return { disabledHours, disabledMinutes };
}
